import { useEffect, useState } from 'react';
import { SupportedCurrencies } from 'vopay';
import { useApi } from '#shared/providers/Api';

export type Balance = {
  AccountBalance: number;
  AvailableFunds: number;
  Currency: SupportedCurrencies;
  PendingFunds: number;
  Reserve: number;
  RollingReserveNSF: number;
  RollingReserveReturns: number;
  RollupAvailableBalance: number | null;
};

// Account balances for shared/TransactionHistory (ID-13367), on the host-injected api via useApi().
// A fresh Direct-Pattern hook rather than a move of client-dashboard's copy (which other host pages still
// use): it auto-fetches on mount and drives its UI off hasBalancesError, because the mount effect can't
// catch a throw; fetchBalances still logs and rethrows so a caller that awaits it directly can catch.
type UseAccountBalancesOptions = {
  /** True when the caller already has what it would read off the balances, so the account route is not called. */
  isSkipped?: boolean;
};

export default function useAccountBalances({ isSkipped = false }: UseAccountBalancesOptions = {}) {
  const api = useApi();
  const [balances, setBalances] = useState<Balance[]>([]);
  const [fetchingBalances, setFetchingBalances] = useState(false);
  const [hasFetchedBalances, setHasFetchedBalances] = useState(false);
  const [hasBalancesError, setHasBalancesError] = useState(false);

  useEffect(() => {
    if (isSkipped) return;

    // Swallow the rejection here: the auto-fetch drives its UI off hasBalancesError, not a throw.
    fetchBalances().catch(() => {});
  }, []);

  async function fetchBalances() {
    try {
      setFetchingBalances(true);
      const response = await api.fetchAccountBalances({});

      if (!response.Success) {
        throw new Error(response.ErrorMessage);
      }

      const parsedBalances = response.AccountBalances.map((balance) => ({
        AccountBalance: balance.AccountBalance,
        AvailableFunds: balance.AvailableFunds,
        Currency: balance.Currency,
        PendingFunds: balance.PendingFunds,
        Reserve: balance.Reserve,
        RollingReserveNSF: balance.RollingReserveNSF ?? 0,
        RollingReserveReturns: balance.RollingReserveReturns ?? 0,
        RollupAvailableBalance: balance.RollupAvailableBalance ?? null,
      }));

      setBalances(parsedBalances);
      setHasBalancesError(false);
    } catch (err) {
      console.error(err);
      setHasBalancesError(true);
      throw err;
    } finally {
      setFetchingBalances(false);
      setHasFetchedBalances(true);
    }
  }

  return {
    fetchingBalances,
    // A skipped fetch counts as settled: the list keys its first request on it.
    hasFetchedBalances: isSkipped || hasFetchedBalances,
    hasBalancesError,
    balances,
    fetchBalances,
  };
}
